import { useCallback, useEffect } from "react";

import { FloatingPortal } from "@floating-ui/react";
import { noop } from "lodash";

import { ErrorBaseInfo } from "src/api/types";
import { Chat } from "src/base-components/Chat/Chat";
import {
  FloatingWindowInternal,
  useFloatingWindowPosition,
} from "src/base-components/FloatingWindow/FloatingWindow";
import { FloatingWindowProps } from "src/base-components/FloatingWindow/store";
import {
  FeatureQueryConversationContext,
  FeatureQueryConversationContextContextTypeEnum,
} from "src/clients/copilot";
import { EventConfigEnriched } from "src/clients/features-control/api";
import { ConversationMessageItem } from "src/copilot/ConversationMessageItem";
import { CopilotTitle } from "src/copilot/CopilotTitle";
import { EmptyState } from "src/copilot/EmptyState";
import { PromptBox } from "src/copilot/PromptBox";
import { QuerySuggestionType } from "src/copilot/QuerySuggestion";
import { TitleHelpContent } from "src/copilot/TitleHelpContent";
import { TitleRightContent } from "src/copilot/TitleRightContent";
import { ToolApprovalResultCard } from "src/copilot/ToolApprovalResultCard";
import { useCopilotConversation } from "src/copilot/useCopilotConversation";
import { getToolApprovalOrResult } from "src/copilot/utils";
import { EntitySchemaResource, useEntitySchemas } from "src/entities/queries";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import {
  tracker,
  trackingEvents,
} from "src/instrumentation/customTrackingEvents";
import { useWorkspaceContext } from "src/router/routerContextHooks";

type FeatureQueryWindowData = {
  conversationId: string;
  onApplyQuery?: (suggestion: QuerySuggestionType) => void;
  resetConversationId: () => void;
  initialPrompt?: string;
  getCurrentQuery: () => string;
  getLastQueryErrorMessage: () => string;
  error?: ErrorBaseInfo;
};

const assertWindowData = (data: unknown): data is FeatureQueryWindowData => {
  return Boolean(
    data &&
      typeof data === "object" &&
      "conversationId" in data &&
      "resetConversationId" in data &&
      "getCurrentQuery" in data &&
      "getLastQueryErrorMessage" in data,
  );
};

export const FeatureQueryCopilotWindow: React.FC<FloatingWindowProps> = ({
  computePosition,
  id,
  data: windowData,
}) => {
  if (!assertWindowData(windowData)) {
    throw new Error("Invalid floating window data");
  }

  const { position, windowRef } = useFloatingWindowPosition(computePosition);
  const { workspace } = useWorkspaceContext();

  const { data: eventSchemas } = useEventSchemas();
  const { data: entitySchemasData } = useEntitySchemas({
    baseUrl: workspace.base_url ?? "",
  });
  const entitySchemas = entitySchemasData?.entities;
  const {
    variables,
    messages,
    data,
    isLoading,
    isError,
    failureCount,
    handleClose,
    onSendMessage,
    handleReset,
    indexToAnimate,
    listRef,
    prompt,
    setPrompt,
  } = useCopilotConversation({
    conversationId: windowData.conversationId,
    windowId: id,
    resetConversationId: windowData.resetConversationId,
  });

  // Initialize prompt with initialPrompt if provided
  useEffect(() => {
    if (windowData.initialPrompt) {
      setPrompt(windowData.initialPrompt);
    }
  }, [windowData.initialPrompt, setPrompt]);

  const handlePromptClick = (
    prompt: string | null,
    mentionedResources: (EntitySchemaResource | EventConfigEnriched)[] = [],
  ) => {
    const context: FeatureQueryConversationContext = {
      context_type:
        FeatureQueryConversationContextContextTypeEnum.FEATURE_QUERY,
      current_query: windowData?.getCurrentQuery(),
      event_schemas: eventSchemas,
      last_error_message: windowData?.getLastQueryErrorMessage(),
      mentioned_events: mentionedResources.filter(
        (res) => "display_name_singular" in res,
      ) as EventConfigEnriched[],
      mentioned_entities: mentionedResources.filter(
        (res) => "_display_name_singular" in res,
      ) as EntitySchemaResource[],
    };
    onSendMessage(prompt, context, undefined);
  };
  const resource = [
    ...(entitySchemas?.map((entity) => ({
      ...entity,
      id: entity._id,
      value: entity._display_name_singular,
    })) ?? []),
    ...(eventSchemas?.map((event) => ({
      ...event,
      id: event.display_name_singular,
      value: event.display_name_singular,
    })) ?? []),
  ];

  const trackCopilotCompletion = useCallback(() => {
    tracker.emit(
      trackingEvents.featureQueryApplySuggestion({
        organization_id: workspace.organization_id,
        applied_query: windowData.getCurrentQuery(),
        conversation_id: windowData.conversationId,
        completion_id: data?.completion_id ?? "",
        prompt: data?.messages[0]?.content ?? "",
      }),
    );
  }, [
    workspace.organization_id,
    windowData,
    data?.completion_id,
    data?.messages,
  ]);

  const loadingMode = (() => {
    if (isLoading && variables?.message === null) return "regenerating";
    if (isLoading && failureCount > 0) return "retrying";
    return "loading";
  })();

  return (
    <FloatingPortal>
      <FloatingWindowInternal
        ref={windowRef}
        clipOverflow={false}
        height="h-150"
        helpText={
          <TitleHelpContent description="Ask questions about your Feature Query and get help with writing an SQL Query, Debugging, and more." />
        }
        isPinned={false}
        maximizable={false}
        style={{
          inset: "0px auto auto 0px",
          transform: position,
        }}
        title={<CopilotTitle />}
        titleRight={<TitleRightContent onReset={handleReset} />}
        draggable
        onClose={handleClose}
      >
        <Chat.Body>
          {data ? (
            <Chat.MessageList ref={listRef}>
              {data.messages.map((message, index) => {
                const toolApprovalOrResult = getToolApprovalOrResult(message);
                if (toolApprovalOrResult) {
                  return (
                    <li key={`${message.role}-${index}`}>
                      <ToolApprovalResultCard toolUse={toolApprovalOrResult} />
                    </li>
                  );
                }

                const isLastMessage = index === data.messages.length - 1;

                return (
                  <ConversationMessageItem
                    key={`${message.role}-${index}`}
                    animate={indexToAnimate === index}
                    canEditFlowVersion={false}
                    completionId={isLastMessage ? data.completion_id : null}
                    conversationId={data.conversation_id}
                    isLastMessage={isLastMessage}
                    isLoading={isLoading}
                    message={message}
                    resourceData={{
                      entities: entitySchemas ?? [],
                      events: eventSchemas ?? [],
                    }}
                    resourceType={
                      FeatureQueryConversationContextContextTypeEnum.FEATURE_QUERY
                    }
                    onApply={(suggestion) => {
                      if (windowData.onApplyQuery) {
                        windowData.onApplyQuery(
                          suggestion as QuerySuggestionType,
                        );
                        trackCopilotCompletion();
                      }
                    }}
                    onRegenerate={() => handlePromptClick(null)}
                    onToolApproval={noop}
                  />
                );
              })}
              {isLoading && <Chat.LoadingIndicator mode={loadingMode} />}
            </Chat.MessageList>
          ) : (
            <EmptyState
              resourceType={
                FeatureQueryConversationContextContextTypeEnum.FEATURE_QUERY
              }
              onPromptClick={handlePromptClick}
            />
          )}
          <Chat.InputArea>
            <PromptBox
              disabled={isLoading || prompt.trim().length === 0}
              errored={isError}
              mentionTextPlaceholder="Use @ to refer to entities and events"
              minified={messages.length > 0}
              resource={resource}
              resourceType={
                FeatureQueryConversationContextContextTypeEnum.FEATURE_QUERY
              }
              value={prompt}
              onChange={(v) => setPrompt(v)}
              onReset={handleReset}
              onSubmit={(content, mentionedResources) => {
                const trimmedContent = content.trim();
                if (trimmedContent) {
                  handlePromptClick(
                    trimmedContent,
                    mentionedResources as (
                      | EntitySchemaResource
                      | EventConfigEnriched
                    )[],
                  );
                  setPrompt("");
                }
              }}
            />
          </Chat.InputArea>
        </Chat.Body>
      </FloatingWindowInternal>
    </FloatingPortal>
  );
};
